import type { LocationQueryRaw } from 'vue-router';
import { SubscriptionPurchasePlatform } from '~/enums/auth.enum';

export async function redirectMiddleware (to: any) {
  const doRedirect = async (path: string, query?: LocationQueryRaw) => {
    clearError();

    return navigateTo({ path, query }, { redirectCode: 301 });
  };

  const supportedLocales = ['en', 'de', 'fr', 'es'] as const;
  const segments = to.path.split('/');
  const firstSegment = segments[1];
  let pathWithoutLocale = to.path;

  if (supportedLocales.includes(firstSegment as typeof supportedLocales[number])) {
    const remainder = segments.slice(2).join('/');
    pathWithoutLocale = '/' + remainder;
  }

  if (pathWithoutLocale.length > 1 && pathWithoutLocale.endsWith('/')) {
    pathWithoutLocale = pathWithoutLocale.slice(0, -1);
  }

  if (pathWithoutLocale.startsWith('/enroll')) {
    const { user } = useUserStore();

    if (user.value?.subscription?.purchasePlatform) {
      const isPurchasePlatformApple = user.value.subscription.purchasePlatform === SubscriptionPurchasePlatform.APPLE;
      const isPurchasePlatformGoogle = user.value.subscription.purchasePlatform === SubscriptionPurchasePlatform.GOOGLE;

      if (isPurchasePlatformApple || isPurchasePlatformGoogle) {
        return doRedirect('/account/subscription', to.query);
      }
    }
  }

  if (pathWithoutLocale === '/' || to.path === '/') {
    return doRedirect('/dashboard', to.query);
  }

  const redirects = {
    '/course-library': '/courses',
    '/enroll': '/enroll/basic-monthly',
    '/enroll/pro': '/enroll/pro-yearly',
    '/user/login': '/auth/login',
    '/user/register': '/auth/signup',
    '/user/password': '/auth/forgot-password',
    '/auth/logout': '/auth/login',
  };

  if (Object.keys(redirects).includes(pathWithoutLocale)) {
    const redirectPath = redirects[pathWithoutLocale as keyof typeof redirects];
    return doRedirect(redirectPath, to.query);
  }

  // If no page is found, check if there is a redirect
  if (to.matched.length === 0) {
    const path = to.path;
    const url = useApiUrl('/redirects?source=' + encodeURIComponent(path));

    try {
      const redirectResponse = await useAuthenticatedFetchSimplified<{destination: string}>(url);

      if (redirectResponse.destination) {
        return doRedirect(redirectResponse.destination, to.query);
      }
    } catch (e) {
      console.error('error fetching redirects', e);
      showError({ statusCode: 404, statusMessage: 'Page not found' });
    }
  }
}

export default defineNuxtRouteMiddleware(to => redirectMiddleware(to));
